import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { ProjectList } from "../helpers/ProjectList";
import GitHubIcon from "@mui/icons-material/GitHub";
import LanguageIcon from "@mui/icons-material/Language";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HomeIcon from "@mui/icons-material/Home";
import ArticleIcon from "@mui/icons-material/Article";
import "../styles/Projects.css";

/* ── Random date seeded per project index ── */
function seedDate(idx) {
  const dates = [
    "Mar 2019", "Aug 2019", "Jan 2020", "Jun 2020", "Oct 2020",
    "Feb 2021", "Jul 2021", "Nov 2021", "Apr 2022", "Sep 2022",
    "Jan 2023", "May 2023", "Oct 2023", "Feb 2024", "Jul 2024",
    "Nov 2024", "Mar 2025", "Jun 2025",
  ];
  return dates[idx % dates.length];
}

/* ── Placeholder tech stacks per category ── */
const TECH_STACKS = {
  software: ["React", "Node.js", "TypeScript", "PostgreSQL", "REST API"],
  cyber:    ["Python", "Kali Linux", "Burp Suite", "Wireshark", "Metasploit"],
  devops:   ["Docker", "Kubernetes", "Terraform", "GitHub Actions", "AWS"],
};

/* ── Tech icon pill (SVG placeholder slot) ── */
function TechPill({ label }) {
  return (
    <span className="tech-tag">
      {/* [PLACEHOLDER] — swap span.tech-icon for <img src={icon} /> */}
      <span className="tech-icon-slot" aria-hidden="true" />
      {label}
    </span>
  );
}

/* ── Category badge config ── */
const CAT_META = {
  software: { label: "Software",    cls: "cat-software" },
  cyber:    { label: "Cyber Sec",   cls: "cat-cyber"    },
  devops:   { label: "DevOps",      cls: "cat-devops"   },
};

const FILTERS = [
  { val: "all",      label: "All"      },
  { val: "software", label: "Software" },
  { val: "cyber",    label: "Cyber"    },
  { val: "devops",   label: "DevOps"   },
];

/* ═══════════════════════════════════════════════════════════════ */
function Projects() {
  const navigate       = useNavigate();
  const [filter, setFilter] = useState("all");

  const visible =
    filter === "all"
      ? ProjectList
      : ProjectList.filter((p) => p.category === filter);

  const countFor = (val) =>
    val === "all"
      ? ProjectList.length
      : ProjectList.filter((p) => p.category === val).length;

  return (
    <div className="projects-page">
      <Helmet>
        <title>Projects | Biko — Security &amp; Engineering Projects</title>
        <meta
          name="description"
          content="Security and engineering projects by Biko — AI fintech platforms, SOC detection systems, secure MERN applications, DevSecOps pipelines, and penetration testing projects."
        />
        <meta property="og:title"       content="Projects | Biko Sulu" />
        <meta property="og:description" content="Offensive tooling, secure systems, and production-ready full-stack applications built by Biko." />
        <meta property="og:url"         content="https://bikosulu.com/projects" />
      </Helmet>

      <div className="projects-container">

        {/* ── Page navigation toggle ── */}
        <nav className="page-nav-toggle">
          <button className="page-nav-btn" onClick={() => navigate("/")}>
            <HomeIcon style={{ fontSize: 15 }} />
            Home
          </button>
          <span className="page-nav-sep">/</span>
          <span className="page-nav-current">Projects</span>
          <span className="page-nav-sep">/</span>
          <button className="page-nav-btn" onClick={() => navigate("/blogs")}>
            <ArticleIcon style={{ fontSize: 15 }} />
            Blog
          </button>
        </nav>

        {/* ── Header ── */}
        <div className="projects-header">
          <span className="projects-eyebrow">Portfolio</span>
          <h1 className="projects-title">
            Full-Stack &amp; <span>Security</span>
          </h1>
          <p className="projects-subtitle">
            Offensive tooling, secure systems, and production-ready applications.
          </p>
        </div>

        {/* ── Filter tabs ── */}
        <div className="project-filters">
          {FILTERS.map((f) => (
            <button
              key={f.val}
              className={`filter-tab ${filter === f.val ? "active" : ""}`}
              onClick={() => setFilter(f.val)}
            >
              {f.label}
              <span className="filter-count">{countFor(f.val)}</span>
            </button>
          ))}
        </div>

        {/* ── Grid ── */}
        <div className="project-grid">
          {visible.map((project) => {
            const realIdx = ProjectList.indexOf(project);
            const cat     = CAT_META[project.category] || { label: project.category, cls: "cat-software" };
            const stack   = TECH_STACKS[project.category] || TECH_STACKS.software;
            const date    = seedDate(realIdx);

            return (
              <article
                key={realIdx}
                className="proj-card"
                onClick={() => navigate(`/project/${realIdx}`)}
                tabIndex={0}
                role="button"
                aria-label={`View ${project.name}`}
                onKeyDown={(e) => e.key === "Enter" && navigate(`/project/${realIdx}`)}
              >
                {/* ── Card top: image ── */}
                <div className="proj-card-img">
                  {project.image ? (
                    <img src={project.image} alt={project.name} loading="lazy" />
                  ) : (
                    <div className="proj-img-placeholder">
                      <span className="proj-placeholder-text">
                        {project.name.slice(0, 2).toUpperCase()}
                      </span>
                      <div className="proj-placeholder-grid" />
                    </div>
                  )}

                  {/* Floating category badge */}
                  <span className={`proj-cat-badge ${cat.cls}`}>{cat.label}</span>

                  {/* Hover overlay CTA */}
                  <div className="proj-img-overlay">
                    <span className="proj-overlay-cta">
                      View Project <ArrowForwardIcon style={{ fontSize: 14 }} />
                    </span>
                  </div>
                </div>

                {/* ── Card body ── */}
                <div className="proj-card-body">

                  <div className="proj-meta-row">
                    <span className="proj-date">{date}</span>
                    <span className="proj-id">
                      #{String(realIdx + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <h2 className="proj-name">{project.name}</h2>
                  <p className="proj-desc">{project.description}</p>

                  {/* Tech stack pills */}
                  <div className="proj-tech-row">
                    {stack.slice(0, 4).map((t) => (
                      <TechPill key={t} label={t} />
                    ))}
                  </div>

                  {/* Action buttons — stop propagation so card click doesn't fire */}
                  <div
                    className="proj-actions"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {project.github && (
                      <a
                        href={project.github}
                        target="_blank"
                        rel="noreferrer"
                        className="proj-btn proj-btn-outline"
                        aria-label="GitHub repo"
                      >
                        <GitHubIcon style={{ fontSize: 15 }} />
                        Repo
                      </a>
                    )}
                    {project.live && (
                      <a
                        href={project.live}
                        target="_blank"
                        rel="noreferrer"
                        className="proj-btn proj-btn-solid"
                        aria-label="Live demo"
                      >
                        <LanguageIcon style={{ fontSize: 15 }} />
                        Live Demo
                      </a>
                    )}
                    <button
                      className="proj-btn proj-btn-ghost"
                      onClick={() => navigate(`/project/${realIdx}`)}
                    >
                      Details <ArrowForwardIcon style={{ fontSize: 13 }} />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Projects;